import { useCallback, useEffect, useRef, useState } from "react";
import { Phone, MessageCircle } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";

const FRAME_COUNT = 161;

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const getFrameSrc = (index: number) =>
  `/hero-scroll-v2/frame-${String(index + 1).padStart(3, "0")}.webp`;

const Hero = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const imagesRef = useRef<Map<number, HTMLImageElement>>(new Map());
  const loadedFramesRef = useRef<Set<number>>(new Set());
  const currentFrameRef = useRef(0);
  const canvasSizeRef = useRef<{ width: number; height: number; dpr: number }>({ width: 0, height: 0, dpr: 1 });
  const [loadProgress, setLoadProgress] = useState(0);
  const [firstReady, setFirstReady] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Content is visible immediately on load (critical for LCP), fades out near the end of the scroll animation
  const contentOpacity = useTransform(
    scrollYProgress,
    [0, 0.55, 0.72, 0.85],
    [1, 1, 1, 0]
  );
  const contentY = useTransform(
    scrollYProgress,
    [0, 0.72, 0.85],
    [0, 0, -30]
  );
  const contentBlur = useTransform(
    scrollYProgress,
    [0, 0.72, 0.85],
    ["0px", "0px", "6px"]
  );
  const contentFilter = useTransform(contentBlur, (b) => `blur(${b})`);

  const measureCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    canvasSizeRef.current = { width, height, dpr };
  }, []);

  const drawFrame = useCallback((frameIndex: number) => {
    const canvas = canvasRef.current;
    const image = imagesRef.current.get(frameIndex);

    if (!canvas || !image?.complete || !image.naturalWidth || !image.naturalHeight) return false;

    const { width, height, dpr } = canvasSizeRef.current;
    if (!width || !height) return false;

    const ctx = canvas.getContext("2d");
    if (!ctx) return false;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    const x = (width - drawWidth) / 2;
    const y = (height - drawHeight) / 2;

    ctx.drawImage(image, x, y, drawWidth, drawHeight);
    return true;
  }, []);

  const preloadFrame = useCallback(
    (frameIndex: number) => {
      const index = Math.round(clamp(frameIndex, 0, FRAME_COUNT - 1));
      const cached = imagesRef.current.get(index);
      if (cached) return cached;

      const image = new Image();
      image.decoding = "async";
      image.onload = () => {
        if (!loadedFramesRef.current.has(index)) {
          loadedFramesRef.current.add(index);
          setLoadProgress(Math.round((loadedFramesRef.current.size / FRAME_COUNT) * 100));
        }

        if (index === 0) {
          setFirstReady(true);
        }

        if (index === currentFrameRef.current || index === 0) {
          drawFrame(currentFrameRef.current);
        }
      };
      image.src = getFrameSrc(index);
      imagesRef.current.set(index, image);

      return image;
    },
    [drawFrame]
  );

  // Use an image sequence rendered from the video. This avoids browser/video seeking bugs online.
  useEffect(() => {
    measureCanvas();
    preloadFrame(0);

    // Sparse frame set — 1 a cada 4 (~40 frames). O scroll usa Math.round(),
    // então frames intermediários caem no cached mais próximo. Reduz decode
    // de 161 imagens webp para ~40, aliviando muito a thread principal.
    const eagerFrames = [8, 16, 24, 32, 48, 64, 80, 96, 112, 128, 144, 160];

    let preloadTimer: number | undefined;
    let idleHandle: number | undefined;

    const startBulkPreload = () => {
      eagerFrames.forEach(preloadFrame);
      preloadTimer = window.setTimeout(() => {
        // Every 4th frame + last frame. ~41 frames total instead of 161.
        for (let i = 0; i < FRAME_COUNT; i += 4) {
          preloadFrame(i);
        }
        preloadFrame(FRAME_COUNT - 1);
      }, 400);
    };

    // Defer heavy preloading until after the page has loaded (protects LCP/FCP).
    const schedule = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
      if (typeof ric === "function") {
        idleHandle = ric(startBulkPreload, { timeout: 1500 });
      } else {
        preloadTimer = window.setTimeout(startBulkPreload, 800);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    // Re-measure on resize (throttled via rAF) instead of on every scroll frame.
    let resizeRaf: number | null = null;
    const onResize = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        measureCanvas();
        drawFrame(currentFrameRef.current);
      });
    };
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      if (preloadTimer) window.clearTimeout(preloadTimer);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (idleHandle && typeof cic === "function") cic(idleHandle);
      window.removeEventListener("load", schedule);
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [drawFrame, measureCanvas, preloadFrame]);

  // React to scroll -> draw the matching extracted video frame.
  useEffect(() => {
    const unsub = scrollYProgress.on("change", (p) => {
      const nextFrame = Math.round(clamp(p) * (FRAME_COUNT - 1));
      currentFrameRef.current = nextFrame;
      preloadFrame(nextFrame);

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (drawFrame(nextFrame)) return;

        for (let distance = 1; distance < 12; distance += 1) {
          const before = nextFrame - distance;
          const after = nextFrame + distance;

          if (before >= 0 && drawFrame(before)) return;
          if (after < FRAME_COUNT && drawFrame(after)) return;
        }
      });
    });
    return () => unsub();
  }, [drawFrame, preloadFrame, scrollYProgress]);

  const progressPct = Math.max(0, Math.min(100, loadProgress));

  return (
    <section
      ref={sectionRef}
      id="home"
      className="relative"
      style={{ height: "300vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center">
        {/* Scroll-controlled hero animation */}
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ background: "hsl(var(--hero-fallback))" }}
        />

        {/* Elegant loader while first frame not ready */}
        <motion.div
          initial={false}
          animate={{ opacity: firstReady ? 0 : 1 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          style={{ pointerEvents: firstReady ? "none" : "auto", background: "hsl(var(--hero-fallback))" }}
          className="absolute inset-0 flex items-center justify-center z-[2]"
        >
          <div className="flex flex-col items-center gap-6">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border border-primary-foreground/15" />
              <div className="absolute inset-0 rounded-full border-t border-primary-foreground/70 animate-spin" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-primary-foreground/60 text-[11px] tracking-[0.3em] uppercase">
                Equilybra
              </span>
              <div className="h-px w-40 bg-primary-foreground/10 overflow-hidden">
                <div
                  className="h-full bg-primary-foreground/70 transition-all duration-300 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Dark overlay */}
        <div
          className="absolute inset-0 z-[1]"
          style={{ background: "rgba(44, 62, 80, 0.45)" }}
        />

        {/* Content — driven entirely by scroll (fade in after start, out before end) */}
        <motion.div
          style={{
            opacity: contentOpacity,
            y: contentY,
            filter: contentFilter,
            willChange: "opacity, transform, filter",
          }}
          className="container mx-auto px-4 relative z-10 pt-32 pb-16"
        >
          <div className="max-w-3xl mx-auto text-center">
            <p className="animated-gradient-text font-semibold uppercase tracking-[0.2em] text-sm mb-4">
              Clínica de Psicologia
            </p>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-primary-foreground mb-6 leading-tight header-glow-text">
              Clínica de Psicologia no{" "}
              <span
                className="header-glow-text"
                style={{
                  color: "#0f2f66",
                  textShadow:
                    "0 2px 12px rgba(255,255,255,0.35), 0 0 24px rgba(120,170,230,0.4)",
                }}
              >
                Ipiranga
              </span>{" "}
              — São Paulo
            </h1>

            <h2 className="text-lg md:text-xl text-white mb-4 font-semibold italic font-quote header-glow-text" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.7), 0 0 24px rgba(0,0,0,0.4)" }}>
              Cuidando da sua saúde mental com acolhimento e expertise
            </h2>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-10 text-sm">
              {[
                "Convênios e Particulares",
                "Atendimento Presencial",
                "Atendimento Online",
                "Parcerias com Empresas e Escolas",
              ].map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 rounded-full bg-black/35 backdrop-blur-md border border-white/30 text-white font-medium hover:bg-black/50 hover:border-white/50 transition-all duration-300"
                  style={{ textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/5511976974905?text=Oi%21%20Vim%20pelo%20site%20da%20Equilybra%20%28Home%29%20e%20gostaria%20de%20saber%20como%20funciona."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl bg-whatsapp text-primary-foreground font-bold text-base hover:bg-whatsapp-hover transition-colors shadow-lg"
              >
                <MessageCircle className="w-5 h-5" />
                Agende pelo WhatsApp
              </a>
              <a
                href="tel:1123050747"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-bold text-base hover:opacity-90 transition-opacity shadow-lg"
              >
                <Phone className="w-5 h-5" />
                Ligue Agora
              </a>
            </div>
          </div>
        </motion.div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent z-[2]" />
      </div>
    </section>
  );
};

export default Hero;
